import { AfterViewInit, Component, ElementRef, inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import * as mapboxgl from 'mapbox-gl';
import { environment } from '../../environments/environment';
import { ensureMapboxWorker } from './mapbox-worker';
import * as turf from '@turf/turf';
import type { Feature, Point } from 'geojson';
import { TRACES_AMBER } from '../company/manage/map/traces-finding-layer';

class CloseDialogControl implements mapboxgl.IControl {
  private _container!: HTMLElement;

  constructor(
    private onClick: () => void,
    private title = 'Close'
  ) {}

  onAdd(map: mapboxgl.Map): HTMLElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mapboxgl-ctrl-icon';
    btn.setAttribute('aria-label', this.title);
    btn.title = this.title;
    btn.style.marginBottom = '-3px';
    btn.innerHTML = `
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.9a1 1 0 0 0 1.41-1.41L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z"/>
      </svg>
    `;
    btn.onclick = this.onClick;

    this._container = document.createElement('div');
    this._container.className = 'mapboxgl-ctrl-group mapboxgl-ctrl';
    this._container.style.zIndex = '9999'; // ensure above
    this._container.appendChild(btn);
    return this._container;
  }

  onRemove(): void {
    this._container?.remove();
  }

  getDefaultPosition(): mapboxgl.ControlPosition {
    return 'top-right';
  }
}

export interface MapViewDialogData {
  geoJson: string;
  /** Circle markers, e.g. a TRACES rejection's `RejectionItem.point` (app#287 Task 8). */
  markers?: [number, number][];
  /** 1-based `RejectionItem.place` indices — outlined as an amber polygon line. */
  highlightPlaces?: number[];
}

@Component({
  selector: 'app-map-view-dialog',
  imports: [MatDialogModule, MatToolbarModule, MatButtonModule, MatIconModule, TranslateModule],
  template: '<mat-dialog-content class="map-dialog-content" #mapContainer></mat-dialog-content>',
  styles: [
    `
      .map-dialog-content {
        height: 100vh;
        padding: 0;
        max-height: 100vh;
        font-size: 10px;
      }
    `,
  ],
})
export class MapViewDialogComponent implements AfterViewInit, OnDestroy {
  data = inject<MapViewDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<MapViewDialogComponent>, { optional: true });
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef<HTMLDivElement>;

  map?: mapboxgl.Map;

  ngAfterViewInit() {
    try {
      ensureMapboxWorker();
      this.map = new mapboxgl.Map({
        accessToken: environment.mapbox.accessToken,
        container: this.mapContainer.nativeElement,
        style: 'mapbox://styles/mapbox/satellite-streets-v12',
        zoom: 2,
        center: [0, 20],
        cooperativeGestures: false,
        dragRotate: true,
        dragPan: true,
      });

      this.map.on('load', () => {
        if (!this.map) return;

        try {
          this.map.resize();
          this.map.addControl(new CloseDialogControl(() => this.dialogRef?.close()), 'top-right');
          this.map.addControl(new mapboxgl.NavigationControl());
          if (this.data.geoJson) {
            this.addGeoJsonToMap(this.data.geoJson);
          }
          this.addTracesFindingsToMap();
        } catch (err) {
          console.error('Failed to load map', err);
        }
      });
    } catch (err) {
      console.error('Failed to initialize map', err);
    }
    setTimeout(() => this.map?.resize(), 100);
  }

  ngOnDestroy() {
    this.map?.remove();
    this.map = undefined;
  }

  private addGeoJsonToMap(geoJsonStr: string) {
    if (!this.map) return;
    const data = JSON.parse(geoJsonStr);

    this.map.addSource('geojson-data', { type: 'geojson', data });
    this.map.addLayer({
      id: 'geojson-fill',
      type: 'fill',
      source: 'geojson-data',
      paint: { 'fill-color': '#00C853', 'fill-opacity': 0.3 },
    });

    this.map.addLayer({
      id: 'geojson-outline',
      type: 'line',
      source: 'geojson-data',
      paint: { 'line-color': '#00C853', 'line-width': 2 },
    });

    const bbox = turf.bbox(data);
    this.map.fitBounds(
      [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]],
      ],
      { padding: 50, maxZoom: 15 }
    );
  }

  /**
   * Amber TRACES markers on the read-only map (app#287 Task 8) — `circle`/`line` layers only,
   * same amber as the map editor's `err-traces-*` layers. `highlightPlaces` is 1-based
   * (`RejectionItem.place`), resolved against the SAME `geoJson` feature collection just added.
   */
  private addTracesFindingsToMap() {
    if (!this.map) return;
    const markers = this.data.markers ?? [];
    const highlightPlaces = this.data.highlightPlaces ?? [];
    if (!markers.length && !highlightPlaces.length) return;

    const pointFeatures: Feature<Point>[] = markers.map((m) => ({
      type: 'Feature',
      properties: {},
      geometry: { type: 'Point', coordinates: m },
    }));

    let outlineFeatures: Feature[] = [];
    if (highlightPlaces.length && this.data.geoJson) {
      try {
        const geo = JSON.parse(this.data.geoJson) as { features?: Feature[] };
        const features = geo.features ?? [];
        outlineFeatures = highlightPlaces
          .map((place) => features[place - 1])
          .filter((f): f is Feature => !!f?.geometry)
          .map((f) => ({ type: 'Feature', properties: {}, geometry: f.geometry }));
      } catch {
        // malformed geoJson — markers still render below
      }
    }

    this.map.addSource('traces-finding-data', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [...outlineFeatures, ...pointFeatures] } as any,
    });
    this.map.addLayer({
      id: 'traces-finding-line',
      type: 'line',
      source: 'traces-finding-data',
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: { 'line-color': TRACES_AMBER, 'line-width': 3 },
    });
    this.map.addLayer({
      id: 'traces-finding-point',
      type: 'circle',
      source: 'traces-finding-data',
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-radius': 8,
        'circle-color': TRACES_AMBER,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    });

    if (pointFeatures.length) {
      this.map.flyTo({ center: pointFeatures[0].geometry.coordinates as [number, number], zoom: 15 });
    } else if (outlineFeatures.length) {
      const bbox = turf.bbox({ type: 'FeatureCollection', features: outlineFeatures } as any);
      this.map.fitBounds(
        [
          [bbox[0], bbox[1]],
          [bbox[2], bbox[3]],
        ],
        { padding: 80, maxZoom: 15 }
      );
    }
  }
}
